import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, switchMap} from 'rxjs';
import {
  IGroupSalaryParams,
  IReceivedData,
  ISalaryInternalAdministration,
} from 'src/app/interfaces/accountant.interfaces';
import {ISalaryRecruiterEdit} from 'src/app/interfaces/add-employee.interface';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IVacationDaysTable} from 'src/app/interfaces/general-tables.interface';
import {
  ISalaryIncreaseData,
  ISalaryIncrease,
  ISalaryIncreasesFilter,
} from 'src/app/interfaces/salary-increase';
import {environment} from 'src/environments/environment';

/**
 * This service work with salary data
 */
@Injectable({
  providedIn: 'root',
})
export class SalaryService {
  url = environment.AccountingAPI + 'api/v1/Salary/';
  payrollUrl = environment.AccountingAPI + 'api/v1/Payment/';

  selectedGroup = '';
  selectedItemFromGroup = '';
  selectedClientsProject = '';
  lastAddedSalaryGroupData!: IGroupSalaryParams;

  constructor(private http: HttpClient) {}

  /**
   * Function should post object {group: 'department', period: '6'}, as response get grouped salary data
   *
   * @param groupParams params like  {group: 'department', period: '6 month'}
   * @returns observable with object of ICurrentSalaryPageObject type
   */
  getGroupSalaryData(
    salaryGroupParams: IGroupSalaryParams
  ): Observable<IReceivedData> {
    salaryGroupParams = this.postObjectTransformer(salaryGroupParams);
    return this.http.post<IReceivedData>(`${this.url}Group`, salaryGroupParams);
  }

  private postObjectTransformer(postObj: IGroupSalaryParams) {
    const newPostObj: IGroupSalaryParams = {...postObj};
    switch (newPostObj.period) {
      case 'Previous month':
        newPostObj.period = '1';
        break;
      case '6 months':
        newPostObj.period = '6';
        break;
      case '12 months':
        newPostObj.period = '12';
        break;
      case 'All':
        newPostObj.period = '0';
        break;
      default:
        newPostObj.period = '';
    }
    switch (newPostObj.group) {
      case 'Employee':
        newPostObj.group = 0;
        break;
      case 'Client':
        newPostObj.group = 1;
        break;
      case 'Project manager':
        newPostObj.group = 2;
        break;
      case 'PM':
        newPostObj.group = 2;
        break;
      case 'Project':
        newPostObj.group = 3;
        break;
      case 'Department':
        newPostObj.group = 4;
        break;
      case 'Unit':
        newPostObj.group = 5;
        break;
    }
    return newPostObj;
  }

  getInternalAdministrationData(
    tabTitle: string,
    range: string
  ): Observable<ISalaryInternalAdministration[]> {
    let titleName = '';
    let period = 0;
    switch (tabTitle) {
      case 'Blood donation':
        titleName = 'BloodDonation';
        break;
      case 'Childbirth':
        titleName = 'ChildBirth';
        break;
      case 'Funeral':
        titleName = 'Funeral';
        break;
      case 'Paid Vacations':
        titleName = 'Vacation';
        break;
      case 'Sickness':
        titleName = 'Sickness';
        break;
      case 'Unpaid Day-offs':
        titleName = 'UnpaidDaysOff';
        break;
      case 'Wedding':
        titleName = 'Wedding';
        break;
    }

    switch (range) {
      case 'Current month':
        period = 1;
        break;
      case '3 months':
        period = 3;
        break;
      case '6 months':
        period = 6;
        break;
      case '12 months':
        period = 12;
        break;
      case 'All':
        period = 0;
        break;
    }

    return this.http.get<ISalaryInternalAdministration[]>(
      `${this.url}InternalAdministration/${titleName}/${period}`
    );
  }

  getEmployeeVacations(employeeId: number): Observable<IVacationDaysTable> {
    return this.http.post<IVacationDaysTable>(
      `${environment.API}api/v1/Vacation/${employeeId}`,
      {}
    );
  }

  getAllEmployeeVacations(
    employeeId: number
  ): Observable<IVacationDaysTable[]> {
    return this.http.get<IVacationDaysTable[]>(
      `${environment.API}api/v1/Vacation/Vacations/${employeeId}`
    );
  }

  generatePayrolls(exchangeRate: string) {
    return this.http.get(`${this.payrollUrl}GeneratePayrolls/${exchangeRate}`);
  }

  getSalaryChanges(
    id: number,
    filter: ISalaryIncreasesFilter
  ): Observable<ISalaryIncreaseData> {
    return this.http.post<ISalaryIncreaseData>(
      `${this.url}${id}/Increase/filter`,
      filter
    );
  }

  addIncreaseSalary(body: ISalaryIncrease, id: number): Observable<void> {
    body.employeeId = id;
    return this.http.post<void>(`${this.url}Increase/Create`, body);
  }

  getIncreaseSalaryReasons(): Observable<IDropdownObject[]> {
    return this.http.get<IDropdownObject[]>(`${this.url}IncreaseReasons`);
  }

  updateVacation(
    body: {id: number; annulationDate: string},
    employeeId: number
  ): Observable<IVacationDaysTable[]> {
    return this.http
      .put<void>(
        `${environment.API}api/v1/Vacation/Update/DateOfAnnulment`,
        body
      )
      .pipe(switchMap(() => this.getAllEmployeeVacations(employeeId)));
  }

  isAvailableSalaryDate(body: {
    employeeId: number;
    date: string;
  }): Observable<boolean> {
    return this.http.post<boolean>(`${this.url}IncreaseDate/IsAvailable`, body);
  }

  getEmployeeSalaryForRecruiter(
    employeeId: number
  ): Observable<ISalaryRecruiterEdit> {
    return this.http.get<ISalaryRecruiterEdit>(
      `${this.url}Recruiter/${employeeId}`
    );
  }

  updateEmployeeSalaryForRecruiter(
    body: ISalaryRecruiterEdit
  ): Observable<void> {
    return this.http.post<void>(`${this.url}Recruiter`, body);
  }
}
